import * as THREE from "three";
import type { Maze } from "../core/maze";
import { DIRECTIONS, type Tile } from "../core/types";
import { step } from "../core/direction";
import { FLOOR_Y, TILE, WALL_HEIGHT, toWorld } from "./layout";

/**
 * The station deck: hull modules, deck plating and the lighting that sells it.
 *
 * Everything static is built once into a handful of instanced meshes, so the
 * ~500 wall cells cost four draw calls rather than five hundred.
 */

const PALETTE = {
  // Hull is the dark mass; the CORRIDORS carry the light. Lighting the wall
  // caps instead reads as a field of glowing tiles with black gaps, which is
  // exactly backwards for a maze.
  hull: 0x333e52,
  hullTrim: 0x46566f,
  strip: 0x2f6f96,
  deck: 0x0c1017,
  deckLit: 0x39607e,
  deckGlow: 0x16405e,
  gate: 0xff4f9a,
} as const;

export interface StationBuild {
  readonly group: THREE.Group;
  dispose(): void;
}

/** True when the neighbouring cell is not hull, i.e. this face is exposed. */
const exposedFaces = (maze: Maze, tile: Tile): number =>
  DIRECTIONS.filter((dir) => maze.kindAt(step(tile, dir)) !== "wall").length;

export const buildStation = (maze: Maze): StationBuild => {
  const group = new THREE.Group();
  const disposables: { dispose(): void }[] = [];

  const track = <T extends { dispose(): void }>(item: T): T => {
    disposables.push(item);
    return item;
  };

  // --- deck plating -------------------------------------------------------
  const deckGeom = track(new THREE.PlaneGeometry(maze.width * TILE, maze.height * TILE));
  const deckMat = track(
    new THREE.MeshStandardMaterial({ color: PALETTE.deck, roughness: 0.85, metalness: 0.35 }),
  );
  const deck = new THREE.Mesh(deckGeom, deckMat);
  deck.rotation.x = -Math.PI / 2;
  deck.position.set(((maze.width - 1) * TILE) / 2, FLOOR_Y - 0.01, ((maze.height - 1) * TILE) / 2);
  deck.receiveShadow = true;
  group.add(deck);

  // --- corridor floor panels ---------------------------------------------
  // A slightly raised, lighter plate under every walkable tile, so the maze
  // reads as corridors between modules rather than holes in a solid block.
  const walkable: Tile[] = [];
  const hull: Tile[] = [];
  const gates: Tile[] = [];
  for (let y = 0; y < maze.height; y += 1) {
    for (let x = 0; x < maze.width; x += 1) {
      const kind = maze.kindAt({ x, y });
      if (kind === "wall") hull.push({ x, y });
      else if (kind === "door") gates.push({ x, y });
      else walkable.push({ x, y });
    }
  }

  const panelGeom = track(new THREE.BoxGeometry(TILE * 0.94, 0.14, TILE * 0.94));
  const panelMat = track(
    new THREE.MeshStandardMaterial({
      color: PALETTE.deckLit,
      // A faint self-lit floor is what makes the corridors read as corridors
      // from an isometric camera, where walls hide most direct light.
      emissive: new THREE.Color(PALETTE.deckGlow),
      emissiveIntensity: 1.35,
      roughness: 0.62,
      metalness: 0.45,
    }),
  );
  const panels = new THREE.InstancedMesh(panelGeom, panelMat, walkable.length);
  panels.receiveShadow = true;
  const matrix = new THREE.Matrix4();
  walkable.forEach((tile, i) => {
    matrix.makeTranslation(tile.x * TILE, FLOOR_Y - 0.06, tile.y * TILE);
    panels.setMatrixAt(i, matrix);
  });
  panels.instanceMatrix.needsUpdate = true;
  group.add(panels);

  // --- hull modules -------------------------------------------------------
  const hullGeom = track(new THREE.BoxGeometry(TILE, WALL_HEIGHT, TILE));
  const hullMat = track(
    new THREE.MeshStandardMaterial({ color: PALETTE.hull, roughness: 0.55, metalness: 0.65 }),
  );
  const hullMesh = new THREE.InstancedMesh(hullGeom, hullMat, hull.length);
  hullMesh.castShadow = true;
  hullMesh.receiveShadow = true;
  const colour = new THREE.Color();
  hull.forEach((tile, i) => {
    matrix.makeTranslation(tile.x * TILE, FLOOR_Y + WALL_HEIGHT / 2, tile.y * TILE);
    hullMesh.setMatrixAt(i, matrix);
    // Vary the plating so a long wall does not read as one flat slab; interior
    // blocks (no exposed face) go darker, as if unlit.
    const exposure = exposedFaces(maze, tile);
    colour.setHex(exposure === 0 ? PALETTE.hull : PALETTE.hullTrim).multiplyScalar(0.72 + 0.09 * exposure);
    hullMesh.setColorAt(i, colour);
  });
  hullMesh.instanceMatrix.needsUpdate = true;
  if (hullMesh.instanceColor !== null) hullMesh.instanceColor.needsUpdate = true;
  group.add(hullMesh);

  // --- hull edge trim -----------------------------------------------------
  // A thin lip around the top of every hull block that faces a corridor. It
  // has to be an unlit *outline*, not a glowing pad: a pad reads as a pellet
  // from this camera, and the deck ends up covered in things that look
  // collectable but are not.
  const exposed = hull.filter((t) => exposedFaces(maze, t) > 0);
  const trimGeom = track(new THREE.BoxGeometry(TILE * 0.995, 0.05, TILE * 0.995));
  const trimMat = track(
    new THREE.MeshStandardMaterial({
      color: PALETTE.strip,
      emissive: new THREE.Color(PALETTE.strip),
      emissiveIntensity: 0.25,
      roughness: 0.4,
      metalness: 0.6,
    }),
  );
  const trim = new THREE.InstancedMesh(trimGeom, trimMat, exposed.length);
  exposed.forEach((tile, i) => {
    matrix.makeTranslation(tile.x * TILE, FLOOR_Y + WALL_HEIGHT + 0.01, tile.y * TILE);
    trim.setMatrixAt(i, matrix);
  });
  trim.instanceMatrix.needsUpdate = true;
  group.add(trim);

  // --- the slop den gate --------------------------------------------------
  const gateGeom = track(new THREE.BoxGeometry(TILE, 0.5, TILE * 0.3));
  const gateMat = track(
    new THREE.MeshStandardMaterial({
      color: PALETTE.gate,
      emissive: new THREE.Color(PALETTE.gate),
      emissiveIntensity: 1.1,
      transparent: true,
      opacity: 0.6,
    }),
  );
  for (const tile of gates) {
    const gate = new THREE.Mesh(gateGeom, gateMat);
    gate.position.copy(toWorld(tile, FLOOR_Y + 0.9));
    group.add(gate);
  }

  return {
    group,
    dispose: () => {
      for (const d of disposables) d.dispose();
    },
  };
};

/** Glowing collectibles. Returned as instanced meshes the game can re-seed. */
export class PelletField {
  readonly group = new THREE.Group();

  private readonly storyGeom: THREE.IcosahedronGeometry;
  private readonly storyMat: THREE.MeshStandardMaterial;
  private readonly quotaGeom: THREE.IcosahedronGeometry;
  private readonly quotaMat: THREE.MeshStandardMaterial;
  private story: THREE.InstancedMesh | null = null;
  private quotaReset: THREE.InstancedMesh | null = null;
  private storyTiles: readonly Tile[] = [];
  private quotaTiles: readonly Tile[] = [];
  private spin = 0;

  // Scratch, reused every frame so the animation allocates nothing.
  private readonly scratch = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly axis = new THREE.Vector3(0, 1, 0);
  private readonly pos = new THREE.Vector3();
  private readonly scale = new THREE.Vector3(1, 1, 1);

  constructor() {
    this.storyGeom = new THREE.IcosahedronGeometry(TILE * 0.17, 0);
    this.storyMat = new THREE.MeshStandardMaterial({
      color: 0x8fe9ff,
      emissive: new THREE.Color(0x35b9ff),
      emissiveIntensity: 2.4,
      roughness: 0.25,
      metalness: 0.2,
    });
    this.quotaGeom = new THREE.IcosahedronGeometry(TILE * 0.28, 1);
    this.quotaMat = new THREE.MeshStandardMaterial({
      color: 0xffd76a,
      emissive: new THREE.Color(0xffa200),
      emissiveIntensity: 1.9,
      roughness: 0.2,
      metalness: 0.3,
    });
  }

  /** Rebuild the instances from the pellets still in play. */
  sync(storyPoints: readonly Tile[], quotaResets: readonly Tile[]): void {
    this.rebuild("story", storyPoints);
    this.rebuild("quotaReset", quotaResets);
  }

  private rebuild(which: "story" | "quotaReset", tiles: readonly Tile[]): void {
    const existing = which === "story" ? this.story : this.quotaReset;
    if (existing !== null) {
      this.group.remove(existing);
      existing.dispose();
    }
    if (which === "story") this.storyTiles = tiles;
    else this.quotaTiles = tiles;
    if (tiles.length === 0) {
      if (which === "story") this.story = null;
      else this.quotaReset = null;
      return;
    }
    const geom = which === "story" ? this.storyGeom : this.quotaGeom;
    const mat = which === "story" ? this.storyMat : this.quotaMat;
    const mesh = new THREE.InstancedMesh(geom, mat, tiles.length);
    const matrix = new THREE.Matrix4();
    const height = which === "story" ? 0.55 : 0.75;
    tiles.forEach((tile, i) => {
      matrix.makeTranslation(tile.x * TILE, FLOOR_Y + height, tile.y * TILE);
      mesh.setMatrixAt(i, matrix);
    });
    // Instanced bounds are computed once; pellets sit all over the deck, so
    // leave culling off rather than have far ones vanish.
    mesh.frustumCulled = false;
    mesh.instanceMatrix.needsUpdate = true;
    this.group.add(mesh);
    if (which === "story") this.story = mesh;
    else this.quotaReset = mesh;
  }

  /**
   * Spin each pellet about its own centre.
   *
   * Setting `rotation`/`scale` on the InstancedMesh itself transforms the whole
   * batch about the mesh's origin, which is the world origin here -- every
   * pellet then swings around the middle of the deck in a wide arc instead of
   * turning on the spot. The per-instance matrix is the only place this belongs.
   */
  update(dt: number): void {
    this.spin += dt;
    this.animate(this.story, this.storyTiles, 0.55, this.spin * 1.2, 1);
    const pulse = 1 + Math.sin(this.spin * 4) * 0.12;
    this.animate(this.quotaReset, this.quotaTiles, 0.75, -this.spin * 0.8, pulse);
  }

  private animate(
    mesh: THREE.InstancedMesh | null,
    tiles: readonly Tile[],
    height: number,
    angle: number,
    scale: number,
  ): void {
    if (mesh === null) return;
    this.quat.setFromAxisAngle(this.axis, angle);
    this.scale.setScalar(scale);
    for (let i = 0; i < tiles.length; i += 1) {
      const tile = tiles[i]!;
      this.pos.set(tile.x * TILE, FLOOR_Y + height, tile.y * TILE);
      this.scratch.compose(this.pos, this.quat, this.scale);
      mesh.setMatrixAt(i, this.scratch);
    }
    mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.storyGeom.dispose();
    this.storyMat.dispose();
    this.quotaGeom.dispose();
    this.quotaMat.dispose();
    this.story?.dispose();
    this.quotaReset?.dispose();
  }
}
